import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Download, RefreshCw, Smartphone } from '../../icons';
import {
  appDownloadFailure, safeDownloadUrl, formatAppSize, getLatestAppRelease, isAppPlatform, requestAppReleaseDownload, shortFingerprint,
  type LatestReleaseResult,
} from '../../api/app-releases';
import { tDl } from '../../lib/app-download-i18n';
import '../../components/AppDownload/AppDownload.css';

/**
 * 휴대폰 받기 화면 — `/app/download/:platform` (2026-09-14 · 시안 `axi-settings-download` 휴대폰 두 장).
 *
 * ★ 설정 › 다운로드의 QR 이 가리키는 곳이다. **로그인이 필요하다** — 라우터가 AuthGuard 로 감싸, 로그인하지 않았으면
 *   로그인한 뒤 이 주소로 돌아온다(`state.from`). 앱 셸(레일 · 메뉴) 없이 휴대폰 폭 한 장으로 선다.
 * ★ 「내려받기」 = `POST /app/releases/:id/download` → 받은 5분짜리 주소로 이동(서버가 감사를 남긴다).
 * ★ iOS 는 배포 인증서 결정 전이라 「준비 중」. 기관이 MDM 배포면 관리자 문의 안내.
 */
type State = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; data: LatestReleaseResult };

export function AppDownloadPage(): JSX.Element {
  const { platform } = useParams<{ platform: string }>();
  const valid = isAppPlatform(platform);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<'other' | 'revoked' | 'gone' | null>(null);

  // 첫 읽기만 캐시를 쓰고, 그 뒤(다시 시도 · 받기 실패 뒤)는 캐시를 건너뛴다.
  const load = useCallback((keepNotice = false, fresh = true) => {
    if (platform !== 'android') return;
    setState({ kind: 'loading' });
    if (!keepNotice) setDownloadError(null);
    (fresh ? getLatestAppRelease('android', { fresh: true }) : getLatestAppRelease('android'))
      .then((data) => setState({ kind: 'ready', data }))
      .catch(() => setState({ kind: 'error' }));
  }, [platform]);
  useEffect(() => { load(false, false); }, [load]);

  const onDownload = async (id: string) => {
    setBusy(true);
    setDownloadError(null);
    try {
      const res = await requestAppReleaseDownload(id);
      const url = safeDownloadUrl(res.downloadUrl);
      if (!url) { setDownloadError('other'); return; }
      window.location.assign(url);
    } catch (err) {
      const why = appDownloadFailure(err);
      if (why === 'revoked' || why === 'gone') { setDownloadError(why); load(true); }
      else if (why === 'mdm') { load(); }
      else setDownloadError('other');
    } finally {
      setBusy(false);
    }
  };

  const release = state.kind === 'ready' ? state.data.release : null;
  const mdm = state.kind === 'ready' && state.data.distribution.mode === 'MDM';

  return (
    <main className="appdl-page appdl">
      <div className="appdl-page__inner">
        <h1 className="appdl-page__title">{tDl('dl.page_title')}</h1>
        <p className="appdl-page__desc">{tDl('dl.desc')}</p>

        {!valid && (
          <div className="appdl-card appdl-note" role="alert">
            <span className="appdl-os appdl-os--muted" aria-hidden><Smartphone size={20} /></span>
            <div><b>{tDl('dl.page_bad_platform')}</b></div>
          </div>
        )}

        {platform === 'ios' && (
          <div className="appdl-card appdl-note" role="note" aria-label={tDl('dl.ios')}>
            <span className="appdl-os appdl-os--muted" aria-hidden><Smartphone size={20} /></span>
            <div><b>{tDl('dl.ios')}</b><span>{tDl('dl.ios_desc')}</span></div>
            <span className="appdl-badge appdl-badge--slate">{tDl('dl.pending')}</span>
          </div>
        )}

        {(downloadError === 'revoked' || downloadError === 'gone') && <p className="appdl-alert" role="alert"><AlertTriangle size={16} aria-hidden />{tDl(downloadError === 'gone' ? 'dl.gone' : 'dl.revoked')}</p>}
        {platform === 'android' && state.kind === 'loading' && <p className="appdl-status" role="status">{tDl('dl.loading')}</p>}

        {platform === 'android' && state.kind === 'error' && (
          <div className="appdl-alert" role="alert">
            <AlertTriangle size={16} aria-hidden /><span>{tDl('dl.error')}</span>
            <button type="button" className="appdl-btn appdl-btn--line appdl-btn--sm" onClick={() => load()}><RefreshCw size={14} aria-hidden />{tDl('dl.retry')}</button>
          </div>
        )}

        {platform === 'android' && state.kind === 'ready' && mdm && (
          <div className="appdl-card appdl-note" role="note" aria-label={tDl('dl.mdm_title')}>
            <span className="appdl-os" aria-hidden><Smartphone size={20} /></span>
            <div>
              <b>{tDl('dl.mdm_title')}</b><span>{tDl('dl.mdm_desc')}</span>
              {state.data.distribution.mdmNote && <span className="appdl-mdmnote">{state.data.distribution.mdmNote}</span>}
            </div>
          </div>
        )}

        {platform === 'android' && state.kind === 'ready' && !mdm && !release && (
          <div className="appdl-card appdl-note" role="note" aria-label={tDl('dl.empty_title')}>
            <span className="appdl-os appdl-os--muted" aria-hidden><Smartphone size={20} /></span>
            <div><b>{tDl('dl.empty_title')}</b><span>{tDl('dl.empty_desc')}</span></div>
          </div>
        )}

        {platform === 'android' && state.kind === 'ready' && !mdm && release && (
          <>
            <article className="appdl-card" aria-label={tDl('dl.android')}>
              <div className="appdl-name">
                <span className="appdl-os" aria-hidden><Smartphone size={20} /></span>
                <div><h3>AXI Works</h3><small>{tDl('dl.android')} · {tDl('dl.latest')} {release.versionName}</small></div>
              </div>
              <dl className="appdl-facts">
                <div><dt>{tDl('dl.fact_version')}</dt><dd className="num">{release.versionName} ({release.versionCode})</dd></div>
                <div><dt>{tDl('dl.fact_size')}</dt><dd className="num">{formatAppSize(release.sizeBytes)}</dd></div>
              </dl>
              <div className="appdl-acts">
                <button type="button" className="appdl-btn" disabled={busy} onClick={() => { void onDownload(release.id); }}>
                  <Download size={16} aria-hidden />{tDl('dl.page_download')}
                </button>
              </div>
              {downloadError === 'other' && <p className="appdl-alert" role="alert"><AlertTriangle size={16} aria-hidden />{tDl('dl.download_failed')}</p>}
            </article>
            <section className="appdl-card" aria-label={tDl('dl.page_how')}>
              <h2>{tDl('dl.page_how')}</h2>
              <ol>
                <li>{tDl('dl.page_step_1')}</li>
                <li>{tDl('dl.page_step_2')}</li>
                <li>{tDl('dl.page_step_3')}</li>
              </ol>
            </section>
            {shortFingerprint(release.signingCertSha256) && (
              <p className="appdl-page__desc appdl-page__sig">{tDl('dl.sig')} <span className="appdl-mono">{shortFingerprint(release.signingCertSha256)}</span></p>
            )}
          </>
        )}

        <Link className="appdl-page__back" to="/">{tDl('dl.page_back')}</Link>
      </div>
    </main>
  );
}
